<script>
  import { emitter } from '@/main.js';
  import fetchWrapper from '@/lib/fetchWrapper.js';
  import SizeVariantOption from '@/SizeVariantOption.vue';
  import WidthVariantOption from '@/WidthVariantOption.vue';
  import ColorVariantOption from '@/ColorVariantOption.vue';
  import PDPImages from '@/PDPImages.vue';
  import PDPPriceBlock from '@/PDPPriceBlock.vue';
  import PDPaddToCart from '@/PDPaddToCart.vue';
  import ProductBadgePDP from '@/ProductBadgePDP.vue';
  import { createApp } from '@/lib/createApp';

  export default {
    name: 'PDPSectionDoubleRefresh',
    data() {
      return {
        handle: '',
        refreshId: '',
        blockId: '',
        isColumnsSection: null,
        useWindowPdpHandle: false,
        boundPdpHandleUpdated: null
      };
    },
    mounted() {
      const parent = this.$el.parentElement;
      this.blockId = parent.getAttribute('data-block-id');
      this.handle = parent.getAttribute('data-handle');
      this.isColumnsSection = parent.closest('[data-columns-refresh-id]');
      this.useWindowPdpHandle =
        Boolean(parent.closest('.mini-product-single')) && !parent.getAttribute('data-block-id');

      // Check if we're mounted in the columns section
      if (this.isColumnsSection) {
        this.refreshId = parent.getAttribute('data-columns-refresh-id');
      } else {
        this.refreshId = parent.getAttribute('data-refresh-id');
      }

      this.boundPdpHandleUpdated = this.useWindowPdpHandle
        ? e => {
            const d = e.detail || {};
            this.handleProductUpdate({ handle: d.handle, ...d });
          }
        : e => {
            this.handleProductUpdate(e);
          };

      if (this.useWindowPdpHandle) {
        window.addEventListener('pdp-handle-updated', this.boundPdpHandleUpdated);
      } else {
        emitter.on('pdp-handle-updated', this.boundPdpHandleUpdated);
      }
    },
    beforeUnmount() {
      if (!this.boundPdpHandleUpdated) {
        return;
      }
      if (this.useWindowPdpHandle) {
        window.removeEventListener('pdp-handle-updated', this.boundPdpHandleUpdated);
      } else {
        emitter.off('pdp-handle-updated', this.boundPdpHandleUpdated);
      }
      this.boundPdpHandleUpdated = null;
    },
    methods: {
      handleProductUpdate(e) {
        if (!e || !e.handle) return;
        if (this.handle === e.handle) return;
        this.handle = e.handle;
        this.updateSectionContent(this.handle, e);
      },
      updateSectionContent(handle, eventData) {
        const updateContent = response => {
          const origSection = new DOMParser().parseFromString(response.result, 'text/html');
          this.updateDOMElements(origSection, response, eventData);
        };

        if (this.refreshId) {
          fetchWrapper.getProductSectionContent(this.refreshId, this.blockId, handle, updateContent);
        }
      },
      updateDOMElements(origSection, response, eventData) {
        const blockRefreshDomId = response.blockId ? `block-refresh-${response.blockId}` : 'block-refresh';
        const blockRefreshSelector = `#${CSS.escape(blockRefreshDomId)}`;
        const updatedBlock = origSection.querySelector(blockRefreshSelector);

        // section refresh section-double.liquid
        const updatedSection = origSection.getElementById('section-refresh');
        const responseSectionId = document.getElementById(response.sectionId);
        const existingSection = document.getElementById('section-refresh');

        // section-refresh-columns - section-columns-pdp-double-refresh.liquid
        const updatedColumnsSection = origSection.getElementById('section-refresh-columns');
        const responseColumnsSectionId = document.getElementById(response.sectionId);
        const existingColumnsSection = document.getElementById('section-refresh-columns');

        // refresh mini pdp on non-product pages
        const updatedMiniPdp = origSection.querySelector('.mini-product-single');
        const sectionWrapperId = `shopify-section-${response.sectionId}`;
        const existingMiniPdp = document.querySelector(`#${sectionWrapperId} .mini-product-single`);
        const isBlockScopedMiniRefresh = Boolean(response.blockId);
        if (updatedMiniPdp && existingMiniPdp && !isBlockScopedMiniRefresh) {
          existingMiniPdp.innerHTML = updatedMiniPdp.innerHTML;
          this.remountVariantOptions(eventData, sectionWrapperId);
        }
        if (response.blockId) {
          const sectionWrapper = document.getElementById(sectionWrapperId);
          const blockHosts = sectionWrapper
            ? [...sectionWrapper.querySelectorAll(`#${CSS.escape(response.blockId)}`)]
            : [];
          if (!blockHosts.length) {
            const single = document.getElementById(response.blockId);
            if (single) {
              blockHosts.push(single);
            }
          }
          blockHosts.forEach(host => {
            host.querySelector(blockRefreshSelector)?.remove();
            if (updatedBlock) {
              host.appendChild(updatedBlock.cloneNode(true));
            }
          });
        }

        // Update section refresh
        if (!this.isColumnsSection && responseSectionId) {
          existingSection?.remove();
          if (updatedSection) {
            responseSectionId.appendChild(updatedSection);
          }
        }

        // Update columns section
        if (this.isColumnsSection && responseColumnsSectionId) {
          existingColumnsSection?.remove();
          if (updatedColumnsSection) {
            responseColumnsSectionId.appendChild(updatedColumnsSection);
          }
        }

        // Trigger height recalculation after DOM update using multiple approaches
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              const event = new CustomEvent('pdp:section:updated', {
                detail: { sectionId: this.refreshId },
                bubbles: true
              });
              document.dispatchEvent(event);
              window.dispatchEvent(new Event('resize'));
              document.dispatchEvent(new Event('theme:resize'));
            } catch (e) {
              // Silently ignore errors during event dispatch
            }
          }, 100);
        });
      },
      remountVariantOptions(eventData, sectionWrapperId) {
        const sectionWrapper = document.getElementById(sectionWrapperId);
        if (!sectionWrapper) return;

        const props = eventData
          ? {
              color: eventData.color,
              name: eventData.color,
              handle: eventData.handle
            }
          : {};

        sectionWrapper.querySelectorAll('.pdp-badge-router').forEach(badgeApp => {
          let appId = badgeApp.getAttribute('id');
          createApp(ProductBadgePDP).mount('#' + appId);
        });
        sectionWrapper.querySelectorAll('.color-variant-options').forEach(colorVariantOption => {
          let colorId = colorVariantOption.getAttribute('id');
          if (colorId) {
            createApp(ColorVariantOption, { previouslySelectedProduct: props }).mount(`#${colorId}`);
          }
        });
        sectionWrapper.querySelectorAll('.size-variant-options').forEach(sizeVariantOption => {
          let sizeId = sizeVariantOption.getAttribute('id');
          if (sizeId) {
            createApp(SizeVariantOption).mount(`#${sizeId}`);
          }
        });
        sectionWrapper.querySelectorAll('.width-variant-options').forEach(widthVariantOption => {
          let widthId = widthVariantOption.getAttribute('id');
          if (widthId) {
            createApp(WidthVariantOption).mount(`#${widthId}`);
          }
        });

        emitter.on('start-full-pdp', e => {
          sectionWrapper.querySelectorAll('.PDPPriceBlock').forEach(priceBlock => {
            let priceBlockId = priceBlock.getAttribute('id');
            createApp(PDPPriceBlock).mount(`#${priceBlockId}`);
          });
          const pdpImages = sectionWrapper.querySelector('#PDPImages');
          if (pdpImages) {
            const Props = { images: e.images, media: e.media, pid: e.pid };
            createApp(PDPImages, Props).mount('#PDPImages');
          }
          const pdpAddToCart = sectionWrapper.querySelectorAll('.PDPaddToCart');
          if (pdpAddToCart) {
            pdpAddToCart.forEach(addToCart => {
              createApp(PDPaddToCart).mount(addToCart);
            });
          }
          emitter.off('start-full-pdp');
        });
      }
    }
  };
</script>

<template>
  <div style="display: none">PDPSectionDoubleRefresh</div>
</template>
